'use strict';

import crLogo from '../public/images/cr_logos/collegerecruiter_logo_497x373.png';
import { decode } from 'he';
/**
 *  Returns a formatted currency value in the designated currency provided;
 *
 * @param {string} currency
 * @param {float} value
 * @param {integer} fractionDigits
 * @return {string}
 */
export const humanizeMonetaryValue = (currency, value, fractionDigits = 2) => {
  let formatter;
  let sign;
  switch (currency) {
    case 'USD':
      // eslint-disable-next-line new-cap
      formatter = Intl.NumberFormat('en-US', { minimumFractionDigits: fractionDigits });
      sign = '$';
    default:
      // eslint-disable-next-line new-cap
      formatter = Intl.NumberFormat('en-US', { minimumFractionDigits: fractionDigits });
      sign = '$';
  }

  return `${sign}${formatter.format(value)}`;
};

export const kebabCase = (string) =>
  string
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/[\s_]+/g, '-')
    .replace(/[^a-zA-Z0-9-]/g, '')
    .toLowerCase();

export const humanizeUnitText = (string, omitYear = false) => {
  // We don't know these values so we wont' display them
  if (string === 'OTHER_COMPENSATION_UNIT' || string === 'COMPENSATION_UNIT_UNSPECIFIED') {
    return '';
  } else {
    const result = string
      ? 'per ' +
        (string.charAt(0).toUpperCase() + string.slice(1).toLowerCase())
          .replace('_', '')
          .replace('ly', '')
      : '';

    return omitYear && result.toLowerCase().includes('year') ? '' : result;
  }
};

export const humanizeEmploymentType = (string) => {
  switch (string) {
    case 'FULL_TIME':
      return 'Full Time';
    case 'PART_TIME':
      return 'Part Time';
    case 'CONTRACTOR':
      return 'Contractor';
    case 'TEMPORARY':
      return 'Temporary';
    case 'INTERNSHIP':
      return 'Internship';
    case 'VOLUNTEER':
      return 'Volunteer';
    case 'PER_DIEM':
      return 'Pre Determined';
    case 'OTHER':
      return 'OTHER';
    default:
      return '';
  }
};

export const getLogoFromHiringOrganization = (hiringOrganization) => {
  // TODO: replace with ENV base url
  const baseUrl = process.env.NEXT_PUBLIC_LOGO_HOST;

  if (hiringOrganization.logo) {
    if (/cr7-images\/banner/.test(hiringOrganization.logo)) {
      return hiringOrganization.logo.replace('{{HOST}}/cr7-images/', baseUrl);
    }

    if (/cr7-images\/company-logo/.test(hiringOrganization.logo)) {
      return hiringOrganization.logo.replace('{{HOST}}/cr7-images/', baseUrl);
    }
  } else {
    return null;
  }
};

export const htmlEntities = (str) => {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
};

export const formatStreetAddress = (address) => {
  const {
    streetAddress = [],
    postalCode = null,
    addressRegion = null,
    addressLocality = null,
    addressCountry = null
  } = address;

  const formattedStreetAddress = streetAddress ? streetAddress.join(' ') : '';

  return `${formattedStreetAddress} ${addressLocality ? addressLocality + ',' : ''} ${
    addressRegion ? addressRegion + ',' : ''
  } ${postalCode ? postalCode + ',' : ''} ${addressCountry ? addressCountry : ''}`;
};

export const nl2br = function (str) {
  return (str + '').replace(/([^>\r\n]?)(\r\n|\n\r|\r|\n)/g, '$1<br />');
};

export const stripTags = function (str) {
  return str.replace(/(<([^>]+)>)/gi, '');
};

export const objectToQuery = (params) => {
  return Object.keys(params)
    .map(function (key) {
      return key + '=' + params[key];
    })
    .join('&');
};

export const formatJobTitle = (str, length) => {
  const { decode } = require('he');

  length = length || 150;
  str = decode(str);
  const strLen = str.length;
  str = str.substr(0, length);
  if (strLen > 150) {
    str.concat('&hellip;');
  }
  return str;
};

export const formatPostTitle = (str, length) => {
  const { decode } = require('he');

  length = length || 150;
  const originalStrLen = str.length;
  str = str.substr(0, length);

  if (str.length < originalStrLen) {
    str = str.substr(0, str.lastIndexOf(' '));
    str = str.concat('&hellip;');
  }
  return decode(str);
};

export const truncateExcerpt = (str = '', len = 220) => {
  if (str.length <= len) {
    return str;
  }
  str = str.substring(0, len);
  str = str.substring(0, str.lastIndexOf(' '));
  str = str.concat('&hellip;');
  return str;
};

export const getCurrentUrl = (router) => {
  const pathname = router.asPath.split('?')[0];
  const url = new URL(
    process.env.NEXT_PUBLIC_APP_HOST + pathname + '?' + objectToQuery(router.query)
  );
  url.searchParams.delete('jobMissing');
  return url;
};

export const now = () => Math.floor(new Date().getTime() / 1000);

const shortEn = { humanizer: null };
export const humanizeDuration = (seconds) => {
  if (!seconds) return '0s';
  seconds = parseInt(seconds) * 1000;

  if (!shortEn.humanizer) {
    const humanizeDuration = require('humanize-duration');
    shortEn.humanizer = humanizeDuration.humanizer({
      language: 'shortEn',
      languages: {
        shortEn: {
          y: () => 'y',
          mo: () => 'mo',
          w: () => 'w',
          d: () => 'd',
          h: () => 'h',
          m: () => 'm',
          s: () => 's',
          ms: () => 'ms'
        }
      }
    });
  }

  return shortEn.humanizer(seconds);
};

export const fetchFeaturedMedia = (wordpressBlogPost) => {
  const defaultImg = {
    source_url: crLogo,
    media_details: {
      height: 373,
      width: 497
    },
    alt_text: 'College Recruiter',
    caption: {
      rendered: ''
    }
  };

  const result = {
    hasFeaturedMedia: false,
    img: defaultImg,
    error: false
  };

  try {
    defaultImg.alt_text = decode(wordpressBlogPost.title.rendered) || 'College Recruiter';
    if (
      wordpressBlogPost._embedded['wp:featuredmedia'][0] &&
      wordpressBlogPost._embedded['wp:featuredmedia'][0].source_url
    ) {
      result.img = wordpressBlogPost._embedded['wp:featuredmedia'][0];
      result.hasFeaturedMedia = true;
    }
  } catch {
    // Just mark this as an error for later use
    result.error = true;
  }
  return result;
};

/**
 *
 * shuffleArray
 * @desc A javascript implementation of the Durstenfeld shuffle, returns a new array
 * @param {*} [array=[]]
 * @return {Array}
 */
export const shuffleArray = (array = []) => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const temp = array[i];
    array[i] = array[j];
    array[j] = temp;
  }
  return array;
};
